import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Plus,
  Mail,
  Trash2,
  Power,
  Edit2,
  X,
  Check,
  Send,
  Inbox,
} from 'lucide-react'
import {
  notificationEmailService,
  NotificationEmail,
  CreateNotificationEmailData,
  UpdateNotificationEmailData,
} from '../../services/notificationEmailService'
import { ConfirmDialog, useConfirmDialog } from '../../components/ui/ConfirmDialog'

export function EmailsPage() {
  const [emails, setEmails] = useState<NotificationEmail[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingEmail, setEditingEmail] = useState<NotificationEmail | null>(null)
  const [formData, setFormData] = useState({ email: '', name: '' })
  const [saving, setSaving] = useState(false)

  const confirmDialog = useConfirmDialog()

  useEffect(() => {
    loadEmails()
  }, [])

  const loadEmails = async () => {
    try {
      setLoading(true)
      const data = await notificationEmailService.getAll()
      setEmails(data)
    } catch (error) {
      toast.error('Error al cargar los emails')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingEmail(null)
    setFormData({ email: '', name: '' })
    setShowForm(true)
  }

  const handleEdit = (email: NotificationEmail) => {
    setEditingEmail(email)
    setFormData({ email: email.email, name: email.name || '' })
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!formData.email.trim()) {
      toast.error('El email es obligatorio')
      return
    }

    // Validar formato email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error('El email no tiene un formato valido')
      return
    }

    setSaving(true)
    try {
      if (editingEmail) {
        const data: UpdateNotificationEmailData = {
          email: formData.email,
          name: formData.name || null,
        }
        await notificationEmailService.update(editingEmail.id, data)
        toast.success('Email actualizado')
      } else {
        const data: CreateNotificationEmailData = {
          email: formData.email,
          name: formData.name || undefined,
        }
        await notificationEmailService.create(data)
        toast.success('Email agregado')
      }
      setShowForm(false)
      setEditingEmail(null)
      loadEmails()
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error al guardar'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (email: NotificationEmail) => {
    try {
      await notificationEmailService.toggleActive(email.id)
      toast.success(email.isActive ? 'Email desactivado' : 'Email activado')
      loadEmails()
    } catch (error) {
      toast.error('Error al cambiar estado')
    }
  }

  const handleDelete = async (email: NotificationEmail) => {
    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar email',
      message: `¿Estas seguro de eliminar "${email.email}"? Ya no recibira notificaciones de solicitudes.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await notificationEmailService.delete(email.id)
      toast.success('Email eliminado')
      loadEmails()
    } catch (error) {
      toast.error('Error al eliminar')
    }
  }

  const activeCount = emails.filter((e) => e.isActive).length

  return (
    <div className="animate-slide-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Send className="h-6 w-6" />
              Emails de Notificacion
            </h1>
            <p className="page-subtitle">
              Configura los correos que recibiran notificaciones cuando se creen nuevas solicitudes
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Agregar Email
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
            <Mail className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {emails.length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total emails
            </p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-success-100 dark:bg-success-900/30">
            <Check className="h-6 w-6 text-success-600 dark:text-success-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {activeCount}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Emails activos
            </p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
        <div className="flex gap-3">
          <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
              Notificaciones por correo
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Todos los emails activos recibiran un correo cada vez que un empleado cree una nueva solicitud de licencia.
            </p>
          </div>
        </div>
      </div>

      {/* Email List */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </div>
      ) : emails.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <Inbox className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay emails configurados
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Agrega emails para recibir notificaciones de nuevas solicitudes
            </p>
            <button onClick={handleCreate} className="btn btn-primary">
              Agregar primer email
            </button>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="divide-y divide-gray-200 dark:divide-slate-700">
            {emails.map((email) => (
              <div
                key={email.id}
                className={`p-4 flex items-center justify-between gap-4 ${
                  !email.isActive ? 'opacity-60 bg-gray-50 dark:bg-slate-800/50' : ''
                }`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`p-2 rounded-full ${
                    email.isActive
                      ? 'bg-primary-100 dark:bg-primary-900/30'
                      : 'bg-gray-100 dark:bg-slate-700'
                  }`}>
                    <Mail className={`h-5 w-5 ${
                      email.isActive
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {email.email}
                    </p>
                    {email.name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {email.name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`badge ${email.isActive ? 'badge-success' : 'badge-gray'}`}>
                    {email.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                  <button
                    onClick={() => handleToggleActive(email)}
                    className={`p-2 rounded-lg transition-colors ${
                      email.isActive
                        ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                        : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={email.isActive ? 'Desactivar' : 'Activar'}
                  >
                    <Power className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(email)}
                    className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(email)}
                    className="p-2 rounded-lg text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingEmail ? 'Editar Email' : 'Agregar Email'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  className="input"
                  placeholder="correo@ejemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre (opcional)
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Ej: RRHH Principal, Administracion..."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Un nombre descriptivo para identificar este correo
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 rounded-b-xl">
              <button
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Guardando...' : editingEmail ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </div>
  )
}
